(() => {
class CollectionFilters {
  constructor() {
    this.filterForm = document.querySelector('.collection-filters');
    this.sortForm = document.querySelector('#SortByForm');
    this.sortSheet = document.querySelector('#CollectionSortSheet');
    this.mobileSortForm = document.querySelector('#SortByFormMobile');
    this.productGrid = document.querySelector('#product-grid');
    this.resultsInfo = document.querySelector('.collection-results-info');
    this.pagination = document.querySelector('.pagination');
    this.clearFiltersBtn = document.querySelector('.clear-filters-btn');
    this.filterOpenButtons = document.querySelectorAll('[data-filter-open]');
    this.filterCloseButtons = document.querySelectorAll('[data-filter-close]');
    this.filterApplyButton = document.querySelector('[data-filter-apply]');
    this.sortOpenButtons = document.querySelectorAll('[data-sort-open]');
    this.sortCloseButtons = document.querySelectorAll('[data-sort-close]');
    this.sheetCloseButtons = document.querySelectorAll('[data-collection-sheet-close]');
    this.sortApplyButton = document.querySelector('[data-sort-apply]');
    this.activeFilterChips = document.querySelector('[data-active-filter-chips]');
    this.filterSheetSubtitle = document.querySelector('[data-filter-sheet-subtitle]');
    this.lastFocusedElement = null;
    this.handlePopstate = this.handlePopstate.bind(this);
    this.filterFocusTrap = window.ThemeFocusTrap && this.filterForm
      ? new window.ThemeFocusTrap(this.filterForm, {
          initialFocus: '[data-filter-close]',
          onEscape: () => this.closeFilterSheet()
        })
      : null;
    this.sortFocusTrap = window.ThemeFocusTrap && this.sortSheet
      ? new window.ThemeFocusTrap(this.sortSheet, {
          initialFocus: '[data-sort-close]',
          onEscape: () => this.closeSortSheet()
        })
      : null;
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.syncPriceFilters();
    this.syncGroupToggles();
    this.updateActiveFilters();
    this.handlePaginationVisibility();
  }

  bindEvents() {
    // Handle filter checkbox changes
    if (this.filterForm) {
      this.filterForm.addEventListener('click', (e) => {
        if (!this.isMobileFilters() && e.target.closest('summary')) {
          e.preventDefault();
        }
      });

      // Price handles: update the label while dragging, commit on release.
      this.filterForm.addEventListener('input', (e) => {
        if (e.target.matches('[data-price-handle]')) {
          this.updatePriceFilter(e.target.closest('[data-filter-price]'), e.target);
        }
      });

      this.filterForm.addEventListener('change', (e) => {
        if (e.target.matches('[data-price-handle]')) {
          this.updatePriceFilter(e.target.closest('[data-filter-price]'), e.target);
          if (!this.isMobileFilters()) this.applyFormFilters();
          return;
        }

        // A category heading without its own tag (e.g. "Occasions") ticks
        // all of its children and applies them as one change.
        if (e.target.matches('[data-filter-group-toggle]')) {
          const children = e.target.closest('.filter-parent')?.querySelectorAll('.filter-children input[type="checkbox"]:not(:disabled)') || [];
          children.forEach(child => {
            child.checked = e.target.checked;
            this.updateFilterOptionState(child);
          });
          this.updateFilterOptionState(e.target);
          if (this.isMobileFilters()) {
            this.updatePendingFilterSummary();
          } else {
            this.applyFormFilters();
          }
          return;
        }

        if (e.target.type === 'checkbox') {
          this.updateFilterOptionState(e.target);
          this.syncGroupToggles();

          if (this.isMobileFilters()) {
            this.updatePendingFilterSummary();
            return;
          }

          this.handleFilterChange(e.target);
          return;
        }

      });

      this.filterForm.addEventListener('toggle', (e) => {
        const currentDetails = e.target;
        if (currentDetails.tagName !== 'DETAILS') {
          return;
        }

        this.handleFilterToggle(currentDetails);
      }, true);
    }

    this.filterOpenButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.openFilterSheet();
      });
    });

    this.filterCloseButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.closeFilterSheet();
      });
    });

    this.sortOpenButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.openSortSheet();
      });
    });

    this.sortCloseButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.closeSortSheet();
      });
    });

    this.sheetCloseButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.closeAllSheets();
      });
    });

    if (this.filterApplyButton) {
      this.filterApplyButton.addEventListener('click', () => {
        this.applyMobileFilters();
      });
    }

    if (this.sortApplyButton) {
      this.sortApplyButton.addEventListener('click', () => {
        this.applyMobileSort();
      });
    }

    // Handle sort form changes (already has onchange="this.form.submit()" but we'll enhance it)
    if (this.sortForm) {
      const sortSelect = this.sortForm.querySelector('#SortBy');
      if (sortSelect) {
        sortSelect.addEventListener('change', () => {
          this.handleSortChange();
        });
      }
    }

    // Handle clear all filters
    if (this.clearFiltersBtn) {
      this.clearFiltersBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.clearAllFilters();
      });
    }

    if (this.activeFilterChips) {
      this.activeFilterChips.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-filter-chip]');
        if (!chip) {
          return;
        }

        e.preventDefault();
        this.loadUrl(chip.href);
      });
    }

    window.addEventListener('popstate', this.handlePopstate);

  }

  handlePopstate() {
    this.loadCollection(window.location.search, false);
  }

  isMobileFilters() {
    return window.matchMedia('(max-width: 768px)').matches;
  }

  openFilterSheet() {
    this.closeSortSheet(false);
    this.lastFocusedElement = document.activeElement;
    document.body.classList.add('filter-sheet-open');
    this.filterForm.setAttribute('role', 'dialog');
    this.filterForm.setAttribute('aria-modal', 'true');
    this.filterOpenButtons.forEach(button => {
      button.setAttribute('aria-expanded', 'true');
    });
    this.filterFocusTrap?.activate(this.lastFocusedElement);
  }

  closeFilterSheet(restoreFocus = true) {
    document.body.classList.remove('filter-sheet-open');
    this.filterForm?.removeAttribute('role');
    this.filterForm?.removeAttribute('aria-modal');
    this.filterFocusTrap?.deactivate({ restoreFocus: false });
    this.filterOpenButtons.forEach(button => {
      button.setAttribute('aria-expanded', 'false');
    });
    if (restoreFocus) {
      this.restoreSheetFocus();
    }
  }

  openSortSheet() {
    this.closeFilterSheet(false);
    this.lastFocusedElement = document.activeElement;
    document.body.classList.add('sort-sheet-open');
    this.sortSheet?.setAttribute('aria-hidden', 'false');
    this.sortOpenButtons.forEach(button => {
      button.setAttribute('aria-expanded', 'true');
    });
    this.sortFocusTrap?.activate(this.lastFocusedElement);
  }

  closeSortSheet(restoreFocus = true) {
    document.body.classList.remove('sort-sheet-open');
    this.sortSheet?.setAttribute('aria-hidden', 'true');
    this.sortFocusTrap?.deactivate({ restoreFocus: false });
    this.sortOpenButtons.forEach(button => {
      button.setAttribute('aria-expanded', 'false');
    });
    if (restoreFocus) {
      this.restoreSheetFocus();
    }
  }

  closeAllSheets() {
    const wasOpen = document.body.classList.contains('filter-sheet-open') || document.body.classList.contains('sort-sheet-open');
    this.closeFilterSheet(false);
    this.closeSortSheet(false);
    if (wasOpen) {
      this.restoreSheetFocus();
    }
  }

  restoreSheetFocus() {
    if (this.lastFocusedElement instanceof HTMLElement) {
      this.lastFocusedElement.focus();
    }
    this.lastFocusedElement = null;
  }

  handleFilterToggle(currentDetails) {
    const currentSummary = currentDetails.querySelector('summary');
    currentSummary?.setAttribute('aria-expanded', currentDetails.open ? 'true' : 'false');

    if (!currentDetails.open || !this.filterForm) {
      return;
    }

    const openDetails = this.filterForm.querySelectorAll('details[open]');
    openDetails.forEach(details => {
      if (details === currentDetails) {
        return;
      }

      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
  }

  applyMobileFilters() {
    const params = new URLSearchParams(window.location.search);
    const sortBy = params.get('sort_by');
    const newParams = new URLSearchParams();

    if (sortBy) {
      newParams.set('sort_by', sortBy);
    }

    this.appendFormFilters(newParams);

    newParams.delete('page');
    this.closeFilterSheet();
    this.loadCollection(newParams.toString(), true);
  }

  // Every ticked, named checkbox plus a set price range. Group toggles have no
  // name and add nothing themselves; their children carry the values.
  appendFormFilters(params) {
    const checkboxes = this.filterForm?.querySelectorAll('input[type="checkbox"][name]:checked') || [];
    checkboxes.forEach(checkbox => {
      params.append(checkbox.name, checkbox.value);
    });

    const priceInputs = this.filterForm?.querySelectorAll('[data-price-param]') || [];
    priceInputs.forEach(input => {
      if (input.value !== '') params.set(input.name, input.value);
    });
  }

  // Desktop applies straight away: rebuild the query from the whole form so a
  // price change or a group toggle is one request.
  applyFormFilters() {
    const params = new URLSearchParams(window.location.search);
    const newParams = new URLSearchParams();
    const sortBy = params.get('sort_by');
    if (sortBy) newParams.set('sort_by', sortBy);

    this.appendFormFilters(newParams);
    this.showLoading();
    this.loadCollection(newParams.toString(), true);
  }

  formatPrice(amount, format) {
    const fixed = Number(amount).toFixed(2);
    const [whole, cents] = fixed.split('.');
    const withSeparators = (separator, decimal) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator) + decimal + cents;
    const values = {
      amount: withSeparators(',', '.'),
      amount_no_decimals: whole.replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      amount_with_comma_separator: withSeparators('.', ','),
      amount_no_decimals_with_comma_separator: whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
      amount_with_apostrophe_separator: withSeparators("'", '.'),
    };
    return (format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => values[key] ?? values.amount);
  }

  // Keeps the two handles from crossing, fills the track between them, writes
  // the label and fills the hidden gte/lte inputs (empty at the range ends).
  updatePriceFilter(container, movedHandle = null) {
    if (!container) return;
    const minHandle = container.querySelector('[data-price-handle="min"]');
    const maxHandle = container.querySelector('[data-price-handle="max"]');
    const rangeMax = Number(container.dataset.rangeMax) || Number(maxHandle.max);
    let min = Number(minHandle.value);
    let max = Number(maxHandle.value);

    if (min > max) {
      if (movedHandle === minHandle) {
        min = max;
        minHandle.value = min;
      } else {
        max = min;
        maxHandle.value = max;
      }
    }

    container.style.setProperty('--price-min', `${(min / rangeMax) * 100}%`);
    container.style.setProperty('--price-max', `${(max / rangeMax) * 100}%`);

    const label = container.querySelector('[data-price-label]');
    const format = container.dataset.moneyFormat;
    if (label) label.textContent = `${this.formatPrice(min, format)} – ${this.formatPrice(max, format)}`;

    const minParam = container.querySelector('[data-price-param="min"]');
    const maxParam = container.querySelector('[data-price-param="max"]');
    if (minParam) minParam.value = min > 0 ? String(min) : '';
    if (maxParam) maxParam.value = max < rangeMax ? String(max) : '';
  }

  syncPriceFilters() {
    this.filterForm?.querySelectorAll('[data-filter-price]').forEach(container => {
      const minHandle = container.querySelector('[data-price-handle="min"]');
      const maxHandle = container.querySelector('[data-price-handle="max"]');
      const rangeMax = Number(container.dataset.rangeMax) || Number(maxHandle.max);
      container.style.setProperty('--price-min', `${(Number(minHandle.value) / rangeMax) * 100}%`);
      container.style.setProperty('--price-max', `${(Number(maxHandle.value) / rangeMax) * 100}%`);
    });
  }

  // A heading checkbox is ticked when all its children are, and shows a
  // partial state when only some are.
  syncGroupToggles() {
    this.filterForm?.querySelectorAll('[data-filter-group-toggle]').forEach(toggle => {
      const children = Array.from(toggle.closest('.filter-parent')?.querySelectorAll('.filter-children input[type="checkbox"]') || []);
      const checked = children.filter(child => child.checked).length;
      toggle.checked = children.length > 0 && checked === children.length;
      toggle.indeterminate = checked > 0 && checked < children.length;
      this.updateFilterOptionState(toggle);
    });
  }

  applyMobileSort() {
    const selectedSort = this.mobileSortForm?.querySelector('input[name="sort_by_mobile"]:checked');
    if (!selectedSort) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    params.set('sort_by', selectedSort.value);
    params.delete('page');

    const desktopSelect = this.sortForm?.querySelector('#SortBy');
    if (desktopSelect) {
      desktopSelect.value = selectedSort.value;
    }

    this.closeSortSheet();
    this.loadCollection(params.toString(), true);
  }

  loadUrl(url) {
    const nextUrl = new URL(url, window.location.origin);
    this.loadCollection(nextUrl.searchParams.toString(), true);
  }

  handleFilterChange(checkbox) {
    // Show loading state
    this.showLoading();

    // Get current URL parameters
    const params = new URLSearchParams(window.location.search);
    const filterName = checkbox.name;
    const filterValue = checkbox.value;

    if (checkbox.checked) {
      // Add filter value
      const existingValues = params.getAll(filterName);
      if (!existingValues.includes(filterValue)) {
        params.append(filterName, filterValue);
      }
    } else {
      // Remove filter value
      const existingValues = params.getAll(filterName);
      params.delete(filterName);
      
      // Re-add all values except the unchecked one
      existingValues.forEach(value => {
        if (value !== filterValue) {
          params.append(filterName, value);
        }
      });
    }

    // Reset to page 1 when filters change
    params.delete('page');

    // Update URL and load new results
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    this.loadCollection(params.toString(), true);
  }

  handleSortChange() {
    this.showLoading();
    
    const sortValue = this.sortForm.querySelector('#SortBy').value;
    const params = new URLSearchParams(window.location.search);
    
    if (sortValue) {
      params.set('sort_by', sortValue);
    } else {
      params.delete('sort_by');
    }

    // Reset to page 1 when sorting changes
    params.delete('page');

    this.loadCollection(params.toString(), true);
  }

  clearAllFilters() {
    this.showLoading();

    // Keep only sort_by parameter if it exists
    const params = new URLSearchParams(window.location.search);
    const sortBy = params.get('sort_by');
    
    const newParams = new URLSearchParams();
    if (sortBy) {
      newParams.set('sort_by', sortBy);
    }

    this.loadCollection(newParams.toString(), true);
  }

  async loadCollection(queryString, updateHistory = true) {
    this.showLoading();
    this.abortController?.abort();
    this.abortController = new AbortController();
    const request = this.abortController;

    try {
      const url = `${window.location.pathname}?${queryString}`;
      
      // Fetch the new page
      const response = await fetch(url, {
        signal: request.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const html = await response.text();
      const parser = new DOMParser();
      const newDocument = parser.parseFromString(html, 'text/html');

      // Update the product grid
      const newProductGrid = newDocument.querySelector('#product-grid');
      if (newProductGrid && this.productGrid) {
        this.productGrid.innerHTML = newProductGrid.innerHTML;
      }

      // Update results info
      const newResultsInfo = newDocument.querySelector('.collection-results-info');
      if (newResultsInfo && this.resultsInfo) {
        this.resultsInfo.innerHTML = newResultsInfo.innerHTML;
      }

      // Update pagination
      const newPagination = newDocument.querySelector('.pagination');
      if (newPagination) {
        // If server returns pagination, update it
        if (this.pagination) {
          this.pagination.innerHTML = newPagination.innerHTML;
          this.pagination.classList.add('has-pages');
        } else {
          // Create pagination element if it doesn't exist
          const paginationContainer = document.createElement('div');
          paginationContainer.className = 'pagination-container';

          const paginationNav = document.createElement('nav');
          paginationNav.className = 'pagination has-pages';
          paginationNav.setAttribute('role', 'navigation');
          paginationNav.setAttribute('aria-label', 'Pagination Navigation');
          paginationNav.innerHTML = newPagination.innerHTML;
          paginationContainer.appendChild(paginationNav);
          
          // Insert after product grid
          this.productGrid.parentNode.insertBefore(paginationContainer, this.productGrid.nextSibling);
          this.pagination = paginationNav;
        }
      } else {
        // No pagination in response, hide existing pagination
        if (this.pagination) {
          this.pagination.classList.remove('has-pages');
          this.pagination.innerHTML = '';
        }
      }

      // Handle pagination visibility after update
      this.handlePaginationVisibility();

      // Update filters to reflect new state
      this.updateFiltersFromResponse(newDocument);
      this.updateSortFromResponse(newDocument);

      // Update URL if needed
      if (updateHistory) {
        window.history.pushState(null, '', url);
      }

      // Trigger custom event for other scripts that might need to know about the update
      window.dispatchEvent(new CustomEvent('collectionUpdated', {
        detail: { url, queryString }
      }));

    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error loading collection:', error);
      this.showError();
    } finally {
      if (this.abortController === request) {
        this.abortController = null;
        this.hideLoading();
      }
    }
  }

  destroy() {
    this.abortController?.abort();
    this.filterFocusTrap?.deactivate({ restoreFocus: false });
    this.sortFocusTrap?.deactivate({ restoreFocus: false });
    window.removeEventListener('popstate', this.handlePopstate);
  }

  updateFiltersFromResponse(newDocument) {
    const newFilters = newDocument.querySelector('.collection-filters');
    if (newFilters && this.filterForm) {
      const newCheckboxes = newFilters.querySelectorAll('input[type="checkbox"]');

      newCheckboxes.forEach(newCheckbox => {
        const currentCheckbox = this.filterForm.querySelector(
          `input[name="${newCheckbox.name}"][value="${newCheckbox.value}"]`
        );
        
        if (currentCheckbox) {
          currentCheckbox.checked = newCheckbox.checked;
          currentCheckbox.disabled = newCheckbox.disabled;
          this.updateFilterOptionState(currentCheckbox);
          
          // Update count text
          const countElement = currentCheckbox.closest('li').querySelector('.filter-count-text');
          const newCountElement = newCheckbox.closest('li').querySelector('.filter-count-text');
          if (countElement && newCountElement) {
            countElement.textContent = newCountElement.textContent;
          }
        }
      });

      // The price block is rebuilt from the response so its handles, label
      // and hidden inputs match the applied range.
      const newPrice = newFilters.querySelector('[data-filter-price]');
      const currentPrice = this.filterForm.querySelector('[data-filter-price]');
      if (newPrice && currentPrice) {
        currentPrice.replaceWith(newPrice);
      }
      this.syncPriceFilters();
      this.syncGroupToggles();

      const newFilterGroups = newFilters.querySelectorAll('details');
      newFilterGroups.forEach(newGroup => {
        const newSummaryText = newGroup.querySelector('summary > span')?.textContent?.trim();
        const currentGroup = Array.from(this.filterForm.querySelectorAll('details')).find(group => {
          return group.querySelector('summary > span')?.textContent?.trim() === newSummaryText;
        });

        if (currentGroup) {
          currentGroup.classList.toggle('is-active', newGroup.classList.contains('is-active'));
          const currentSummary = currentGroup.querySelector('summary');
          const newSummary = newGroup.querySelector('summary');
          if (currentSummary && newSummary) {
            currentSummary.innerHTML = newSummary.innerHTML;
          }
        }
      });

      // Update clear filters button - FIXED LOGIC
      const newClearContainer = newDocument.querySelector('.clear-filters-container');
      const currentClearContainer = document.querySelector('.clear-filters-container');
      
      if (newClearContainer) {
        if (currentClearContainer) {
          currentClearContainer.innerHTML = newClearContainer.innerHTML;
        } else {
          const clearContainer = document.createElement('div');
          clearContainer.className = 'clear-filters-container';
          clearContainer.innerHTML = newClearContainer.innerHTML;
          const footer = this.filterForm.querySelector('.filter-sheet-footer');
          this.filterForm.insertBefore(clearContainer, footer);
        }
        
        this.clearFiltersBtn = document.querySelector('.clear-filters-btn');
        if (this.clearFiltersBtn) {
          this.clearFiltersBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.clearAllFilters();
          });
        }
      } else {
        if (currentClearContainer) {
          currentClearContainer.remove();
        }
        this.clearFiltersBtn = null;
      }
    }

    this.updateActiveFilterChips(newDocument);
    this.updateActiveFilters();
  }

  updateSortFromResponse(newDocument) {
    const newSortSelect = newDocument.querySelector('#SortBy');
    const currentSortSelect = this.sortForm?.querySelector('#SortBy');
    if (!newSortSelect) {
      return;
    }

    if (currentSortSelect) {
      currentSortSelect.value = newSortSelect.value;
    }

    this.mobileSortForm?.querySelectorAll('input[name="sort_by_mobile"]').forEach(input => {
      input.checked = input.value === newSortSelect.value;
    });
  }

  updateActiveFilters() {
    const filterDetails = this.filterForm?.querySelectorAll('details');
    
    filterDetails?.forEach(details => {
      const checkboxes = details.querySelectorAll('input[type="checkbox"]:checked');
      const summary = details.querySelector('summary');
      
      if (checkboxes.length > 0) {
        details.classList.add('is-active');
      } else {
        details.classList.remove('is-active');
      }

      summary?.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    });

    this.updatePendingFilterSummary();
  }

  updateFilterOptionState(checkbox) {
    const option = checkbox.closest('.filter-option');
    if (!option) {
      return;
    }

    option.classList.toggle('is-active', checkbox.checked);
    option.classList.toggle('is-disabled', checkbox.disabled && !checkbox.checked);
  }

  updatePendingFilterSummary() {
    const checkedCount = this.filterForm?.querySelectorAll('input[type="checkbox"]:checked').length || 0;
    const activeCount = checkedCount;

    if (this.filterSheetSubtitle) {
      this.filterSheetSubtitle.textContent = activeCount > 0
        ? `${activeCount} filtera primijenjeno`
        : 'Odaberite opcije za uži izbor';
    }
  }

  updateActiveFilterChips(newDocument) {
    const newChips = newDocument.querySelector('[data-active-filter-chips]');

    if (newChips && this.activeFilterChips) {
      this.activeFilterChips.innerHTML = newChips.innerHTML;
      const hasActiveChips = this.activeFilterChips.querySelector('[data-filter-chip]');
      this.activeFilterChips.classList.toggle('is-empty', !hasActiveChips);
    }
  }

  handlePaginationVisibility() {
    if (this.pagination) {
      const paginationLinks = this.pagination.querySelectorAll('.pagination__link');
      const hasMultiplePages = paginationLinks.length > 1;
      
      if (hasMultiplePages) {
        this.pagination.classList.add('has-pages');
      } else {
        this.pagination.classList.remove('has-pages');
      }
    }
  }

  showLoading() {
    if (this.productGrid) {
      this.productGrid.classList.add('loading');
      this.productGrid.style.opacity = '0.6';
      this.productGrid.style.pointerEvents = 'none';
    }

    const formElements = document.querySelectorAll('.collection-filters input, #SortBy, #SortByFormMobile input');
    formElements.forEach(element => {
      element.disabled = true;
    });
  }

  hideLoading() {
    if (this.productGrid) {
      this.productGrid.classList.remove('loading');
      this.productGrid.style.opacity = '';
      this.productGrid.style.pointerEvents = '';
    }

    const formElements = document.querySelectorAll('.collection-filters input, #SortBy, #SortByFormMobile input');
    formElements.forEach(element => {
      element.disabled = false;
    });

    const checkboxes = document.querySelectorAll('.collection-filters input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      const countText = checkbox.closest('li')?.querySelector('.filter-count-text')?.textContent;
      if (countText && countText.includes('(0)') && !checkbox.checked) {
        checkbox.disabled = true;
      }
    });
  }

  showError() {
    if (this.resultsInfo) {
      this.resultsInfo.innerHTML = '<p class="error-message" style="color: #dc3545;">Sorry, there was an error loading the products. Please try again.</p>';
    }
  }
}

const initCollectionFilters = (scope = document) => {
  const filterForm = scope.querySelector('.collection-filters');
  if (!filterForm || window.collectionFilters?.filterForm === filterForm) return;

  window.collectionFilters?.destroy();
  window.collectionFilters = new CollectionFilters();
};

window.initCollectionFilters = initCollectionFilters;
initCollectionFilters();

if (!window.collectionFiltersLifecycleBound) {
  document.addEventListener('shopify:section:load', (event) => {
    window.initCollectionFilters?.(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.querySelector('.collection-filters')) {
      window.collectionFilters?.destroy();
      window.collectionFilters = null;
    }
  });
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.pagination__link:not(.pagination__link--current)');
    if (!link || !window.collectionFilters) return;

    event.preventDefault();
    const url = new URL(link.href);
    window.collectionFilters.loadCollection(url.searchParams.toString(), true);
  });
  window.collectionFiltersLifecycleBound = true;
}
})()
